import api from './api';
import { API_ENDPOINTS } from '../config/api';

export const cartService = {
  /**
   * Get user's cart from backend
   * Works for both logged-in users (JWT) and guest users (session)
   * @returns {Promise<Object>} Cart data with items and totals
   */
  getCart: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.CART.GET);
      return {
        success: true,
        cart: response.data.cart || response.data,
        totals: response.data.totals || null
      };
    } catch (error) {
      console.error('Error fetching cart:', error);
      // Return empty cart on error (new user/guest)
      if (error.response?.status === 404) {
        return {
          success: true,
          cart: { items: [], total_items: 0, subtotal: '0.00' },
          totals: { subtotal: '0.00', item_count: 0, currency: 'INR' }
        };
      }
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch cart',
        cart: { items: [], total_items: 0, subtotal: '0.00' }
      };
    }
  },

  /**
   * Add item to cart
   * @param {number} productId - Product ID to add
   * @param {number} quantity - Quantity to add (default: 1)
   * @returns {Promise<Object>} Added cart item details
   */
  addToCart: async (productId, quantity = 1) => {
    try {
      const response = await api.post(API_ENDPOINTS.CART.ADD_ITEM, {
        product_id: productId,
        quantity: quantity
      });
      return {
        success: true,
        message: response.data.message || 'Item added to cart',
        item: response.data.item,
        data: response.data
      };
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.product_id?.[0] ||
                          error.response?.data?.detail ||
                          'Failed to add item to cart';
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Update cart item quantity
   * @param {number} cartItemId - Cart item ID to update
   * @param {number} quantity - New quantity (set to 0 to remove)
   * @returns {Promise<Object>} Updated cart item details
   */
  updateCartItem: async (cartItemId, quantity) => {
    try {
      const endpoint = `${API_ENDPOINTS.CART.UPDATE_ITEM}/${cartItemId}/update_item/`;
      const response = await api.patch(endpoint, { quantity });

      // If quantity is 0, item is removed
      if (quantity === 0) {
        return {
          success: true,
          message: 'Cart item removed successfully',
          removed: true
        };
      }

      return {
        success: true,
        message: response.data.message || 'Cart item updated',
        item: response.data.item,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating cart item:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update cart item'
      };
    }
  },

  /**
   * Remove item from cart
   * @param {number} cartItemId - Cart item ID to remove
   * @returns {Promise<Object>} Success/failure status
   */
  removeFromCart: async (cartItemId) => {
    try {
      const endpoint = `${API_ENDPOINTS.CART.REMOVE_ITEM}/${cartItemId}/remove_item/`;
      await api.delete(endpoint);
      return {
        success: true,
        message: 'Item removed from cart'
      };
    } catch (error) {
      console.error('Error removing from cart:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove item from cart'
      };
    }
  },

  /**
   * Clear all items from cart
   * @returns {Promise<Object>} Success/failure status
   */
  clearCart: async () => {
    try {
      await api.delete(`${API_ENDPOINTS.CART.CLEAR}clear/`);
      return {
        success: true,
        message: 'Cart cleared successfully'
      };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to clear cart'
      };
    }
  },

  /**
   * Validate cart for checkout
   * Checks stock availability for all items
   * @returns {Promise<Object>} Validation result
   */
  validateCheckout: async () => {
    try {
      const response = await api.post(API_ENDPOINTS.CART.VALIDATE_CHECKOUT);
      return {
        success: true,
        valid: response.data.valid,
        data: response.data
      };
    } catch (error) {
      console.error('Error validating checkout:', error);
      return {
        success: false,
        valid: false,
        error: error.response?.data?.error || 'Cart validation failed',
        details: error.response?.data?.errors || error.response?.data?.details || []
      };
    }
  },

  /**
   * Merge current guest cart into logged-in user cart
   * Requires JWT token and guest session cookie
   */
  mergeGuestCart: async () => {
    try {
      const response = await api.post(API_ENDPOINTS.CART.MERGE_GUEST);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error merging guest cart:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to merge guest cart'
      };
    }
  }
};

export default cartService;
